import { Check } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import RichTemplateEditor from "../RichTemplateEditor"

const TemplateEditorForm = ({ initial, onSave, onCancel, expanded, onToggleExpanded }) => {
  const { t } = useTranslation()

  const [name, setName] = useState(initial?.name || "")
  const [body, setBody] = useState(initial?.body || "")
  const [isDefault, setIsDefault] = useState(!!initial?.is_default)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const isOwn = initial ? initial.own : true
  const readOnly = initial && !isOwn

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!name.trim() || !body.trim()) {
      setError(t("saved_tours.share.errors.required"))
      return
    }
    setSaving(true)
    setError(null)
    try {
      // "html" needs to be added to TravelBook::TourShareTemplate::FORMATS on the backend
      await onSave({ name: name.trim(), format: "html", body, is_default: isDefault })
    } catch (err) {
      setError(err?.data?.message || t("saved_tours.share.errors.save_failed"))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div>
        <label className="block text-xs font-medium text-[#9AA5B4] mb-1">
          {t("saved_tours.share.name")}
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={readOnly}
          placeholder={t("saved_tours.share.name_placeholder")}
          className="w-full px-3 py-2 rounded-xl border border-[#E7EAEF] text-sm text-[#1F2937]
                     focus:outline-none focus:ring-2 focus:ring-[#1F2937]/10 disabled:bg-[#F4F6F9]"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-[#9AA5B4] mb-1.5">
          {t("saved_tours.share.body")}
        </label>
        <RichTemplateEditor
          value={body}
          onChange={setBody}
          readOnly={readOnly}
          expanded={expanded}
          onToggleExpanded={onToggleExpanded}
        />
      </div>

      {!readOnly && (
        <label className="flex items-center gap-2 text-sm text-[#1F2937] cursor-pointer select-none">
          <button
            type="button"
            onClick={() => setIsDefault((v) => !v)}
            className={`w-4.5 h-4.5 flex items-center justify-center rounded-md border transition-colors ${
              isDefault
                ? "bg-[#1F2937] border-[#1F2937] text-white"
                : "border-[#E7EAEF] text-transparent"
            }`}
          >
            <Check size={12} strokeWidth={3} />
          </button>
          {t("saved_tours.share.make_default")}
        </label>
      )}

      {error && <div className="text-xs text-red-500">{error}</div>}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-3.5 py-2 rounded-xl text-sm font-medium text-[#9AA5B4] hover:bg-[#F4F6F9] transition-colors"
        >
          {t("saved_tours.share.cancel")}
        </button>
        {!readOnly && (
          <button
            type="submit"
            disabled={saving}
            className="px-3.5 py-2 rounded-xl text-sm font-medium text-white bg-[#1F2937]
                       hover:bg-[#111827] transition-colors disabled:opacity-50"
          >
            {saving ? t("saved_tours.share.saving") : t("saved_tours.share.save")}
          </button>
        )}
      </div>
    </form>
  )
}

export default TemplateEditorForm
